import { baseAPI } from 'api/baseApi'
import {
 TCreateDomainBody,
 TCreateDomainResponse,
 TDnsRecord,
 TDnsRecordBody,
 TDomainScanResponse,
 TDomainStatus,
 TZoneRecord,
} from '../types'
import { toScanRecords, unwrapResponse } from '../utils'

// DigitalOcean returns name/data; the UI works with host/value everywhere
const toDnsRecord = (r: TZoneRecord): TDnsRecord => ({
 id: r.id,
 type: r.type,
 host: r.host ?? r.name ?? '',
 value: r.value ?? r.data ?? '',
 priority: r.priority ?? null,
 protected: !!r.protected,
})

// raw: true skips the global status check; responses may be plain JSON or
// wrapped in { status, data }, so each endpoint unwraps both
// domains/* live under user/; domain-status stays at the root (Neo calls it too)
export const domainApi = baseAPI.injectEndpoints({
 endpoints: ({ query, mutation }) => ({
  // Page state (empty / waiting / connected); reflects the last cron check
  getDomainStatus: query<TDomainStatus, void>({
   query: () => ({ url: 'domain-status', method: 'get', raw: true }),
   transformResponse: (res) => unwrapResponse<TDomainStatus>(res),
   providesTags: ['DOMAIN_STATUS'],
  }),
  // Stateless: registrar instructions + current records, nothing saved
  scanDomain: mutation<TDomainScanResponse, { domain: string }>({
   query: (body) => ({
    url: 'user/domains/scan',
    method: 'post',
    body,
    raw: true,
   }),
   // Records come back in raw DNS shape; map them to { type, host, value }
   transformResponse: (res, _meta, { domain }) => {
    const data = unwrapResponse<TDomainScanResponse>(res)
    return { ...data, records: toScanRecords(data?.records, domain) }
   },
  }),
  // Persists the domain and creates the DNS zone with the confirmed records
  createDomain: mutation<TCreateDomainResponse, TCreateDomainBody>({
   query: (body) => ({ url: 'user/domains', method: 'post', body, raw: true }),
   transformResponse: (res) => unwrapResponse<TCreateDomainResponse>(res),
   invalidatesTags: ['DOMAIN_STATUS'],
  }),
  // Removes a domain row (used to delete a redirecting domain)
  deleteDomain: mutation<unknown, number>({
   query: (id) => ({ url: `user/domains/${id}`, method: 'delete', raw: true }),
   invalidatesTags: ['DOMAIN_STATUS', 'DOMAIN_RECORDS'],
  }),
  // Live zone records; each carries the server's protected flag
  getDomainRecords: query<TDnsRecord[], number>({
   query: (id) => ({
    url: `user/domains/${id}/records`,
    method: 'get',
    raw: true,
   }),
   transformResponse: (res) =>
    (unwrapResponse<{ records?: TZoneRecord[] }>(res)?.records ?? []).map(
     toDnsRecord,
    ),
   providesTags: ['DOMAIN_RECORDS'],
  }),
  addDomainRecord: mutation<unknown, { id: number; body: TDnsRecordBody }>({
   query: ({ id, body }) => ({
    url: `user/domains/${id}/records`,
    method: 'post',
    body,
    raw: true,
   }),
   invalidatesTags: ['DOMAIN_RECORDS'],
  }),
  updateDomainRecord: mutation<
   unknown,
   { id: number; recordId: number; body: TDnsRecordBody }
  >({
   query: ({ id, recordId, body }) => ({
    url: `user/domains/${id}/records/${recordId}`,
    method: 'put',
    body,
    raw: true,
   }),
   invalidatesTags: ['DOMAIN_RECORDS'],
  }),
  deleteDomainRecord: mutation<unknown, { id: number; recordId: number }>({
   query: ({ id, recordId }) => ({
    url: `user/domains/${id}/records/${recordId}`,
    method: 'delete',
    raw: true,
   }),
   invalidatesTags: ['DOMAIN_RECORDS'],
  }),
 }),
})

export const {
 useGetDomainStatusQuery,
 useLazyGetDomainStatusQuery,
 useScanDomainMutation,
 useCreateDomainMutation,
 useDeleteDomainMutation,
 useGetDomainRecordsQuery,
 useAddDomainRecordMutation,
 useUpdateDomainRecordMutation,
 useDeleteDomainRecordMutation,
} = domainApi
